/**
 * conversion.ts — disparo de eventos de CONVERSIÓN a las plataformas de ads
 * cargadas client-side (ver AnalyticsTags.tsx): Meta Pixel (fbq), GA4 (gtag) y
 * GTM (dataLayer). Distinto de `analytics.ts` (analítica first-party propia al
 * panel admin): esto alimenta las campañas de Meta/Google.
 *
 * Todo best-effort: si un tag no está configurado/cargado, la llamada es no-op y
 * NUNCA rompe el flujo. En una SPA los eventos NO se disparan solos al navegar por
 * rutas del cliente: hay que llamarlos explícitamente en el momento correcto (ej.
 * CompleteRegistration cuando el backend confirma el alta, no al apretar el botón).
 */

interface ConversionWindow extends Window {
  fbq?: (...args: unknown[]) => void;
  gtag?: (...args: unknown[]) => void;
  dataLayer?: unknown[];
}

function win(): ConversionWindow | null {
  return typeof window === 'undefined' ? null : (window as ConversionWindow);
}

type Params = Record<string, unknown>;

/** Meta Pixel — evento ESTÁNDAR (CompleteRegistration, StartTrial, …). */
export function fbqTrack(event: string, params?: Params, opts?: { eventID?: string }): void {
  try {
    win()?.fbq?.('track', event, params ?? {}, opts);
  } catch {
    /* tag ausente o error: ignorar */
  }
}

/** Meta Pixel — evento CUSTOM (RegistroIniciado, RegistroPaso1Rubro, …). */
export function fbqTrackCustom(event: string, params?: Params): void {
  try {
    win()?.fbq?.('trackCustom', event, params ?? {});
  } catch {
    /* ignorar */
  }
}

/** GA4 (gtag) event. */
export function gaEvent(name: string, params?: Params): void {
  try {
    win()?.gtag?.('event', name, params ?? {});
  } catch {
    /* ignorar */
  }
}

/** GTM dataLayer push (para reglas/forwarding server-side de GTM). */
export function dlPush(obj: Params): void {
  const w = win();
  if (!w) return;
  try {
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push(obj);
  } catch {
    /* ignorar */
  }
}

/**
 * Genera un event_id para deduplicar el pixel del navegador contra la
 * Conversions API (server-side). Se pasa como `eventID` a fbq y — cuando se
 * implemente CAPI — con el MISMO valor al backend.
 */
export function newEventId(): string {
  try {
    const c = (globalThis as { crypto?: Crypto }).crypto;
    if (c?.randomUUID) return c.randomUUID();
  } catch {
    /* fallthrough */
  }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

// ===========================================================================
//  Eventos del embudo de REGISTRO (un lugar único por hito)
// ===========================================================================

/** Entró a la pantalla de registro. */
export function trackRegistrationStarted(): void {
  fbqTrackCustom('RegistroIniciado');
  gaEvent('registro_iniciado');
  dlPush({ event: 'registro_iniciado' });
}

/** Completó el paso de rubro (vertical). */
export function trackRegistrationVertical(verticalName?: string): void {
  const p = verticalName ? { content_category: verticalName } : undefined;
  fbqTrackCustom('RegistroPaso1Rubro', p);
  gaEvent('registro_paso1_rubro', verticalName ? { item_category: verticalName } : undefined);
  dlPush({ event: 'registro_paso1_rubro', vertical: verticalName ?? null });
}

/** Completó el paso de plan. GA4 equivalente: begin_checkout. */
export function trackRegistrationPlan(planName?: string, currency = 'PYG'): void {
  fbqTrackCustom('RegistroPaso2Plan', planName ? { content_name: planName } : undefined);
  gaEvent('begin_checkout', {
    currency,
    value: 0,
    items: planName ? [{ item_name: planName }] : undefined,
  });
  dlPush({ event: 'begin_checkout', plan: planName ?? null, currency, value: 0 });
}

/**
 * Registro COMPLETADO con éxito (backend confirmó el alta). Dispara la conversión
 * CompleteRegistration + StartTrial (empieza la prueba de 30 días) en Meta, y su
 * equivalente GA4 (sign_up) + dataLayer. `eventId` deduplica contra la CAPI.
 */
export function trackRegistrationComplete(opts: { currency: string; eventId: string }): void {
  const { currency, eventId } = opts;
  fbqTrack(
    'CompleteRegistration',
    { content_name: 'registro_empresa', status: true, value: 0, currency },
    { eventID: eventId },
  );
  fbqTrack('StartTrial', { value: 0, currency }, { eventID: `${eventId}-trial` });
  gaEvent('sign_up', { method: 'email' });
  dlPush({ event: 'registro_completado', currency, value: 0, event_id: eventId });
}
